import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Layout } from '~/eds/src/components/Layout/Layout';
import { NumberInput } from '~/eds/src/components/NumberInput/NumberInput';
import { Select } from '~/eds/src/components/Select/Select';
import { Filter } from '~/evifields/src/types/Filter';
import { NumberValue } from '~/evifields/src/types/Value';

interface Props {
  filter: Filter<NumberValue>;
  onChange: (updatedValues: NumberValue[]) => void;
}

export const AgeValues = ({ filter, onChange }: Props) => {
  const intl = useIntl();
  const { id, operatorId, values } = filter;
  const [min = null, max = null] = values;

  const [unitValue, setUnitValue] = useState(1);
  const [minValue, setMinValue] = useState(min);
  const [maxValue, setMaxValue] = useState(max);

  useEffect(() => {
    if (operatorId === 'between') {
      const adjustedMin = minValue ? minValue * unitValue : null;
      const adjustedMax = maxValue ? maxValue * unitValue : null;
      onChange([adjustedMin, adjustedMax]);
    } else {
      const adjustedValue = minValue ? minValue * unitValue : null;
      onChange(minValue ? [adjustedValue] : []);
    }
  }, [minValue, maxValue, unitValue, operatorId]);

  const options = [
    {
      label: intl.formatMessage({
        id: 'AgeValues.dayLabel',
        defaultMessage: 'Day',
        description: 'Time unit option for day',
      }),
      value: 1,
    },
    {
      label: intl.formatMessage({
        id: 'AgeValues.weekLabel',
        defaultMessage: 'Week',
        description: 'Time unit option for week',
      }),
      value: 7,
    },
    {
      label: intl.formatMessage({
        id: 'AgeValues.monthLabel',
        defaultMessage: 'Month',
        description: 'Time unit option for month',
      }),
      value: 30,
    },
    {
      label: intl.formatMessage({
        id: 'AgeValues.yearLabel',
        defaultMessage: 'Year',
        description: 'Time unit option for year',
      }),
      value: 365,
    },
  ];

  const inputs = () => {
    switch (operatorId) {
      case 'between': {
        return (
          <>
            <NumberInput
              name={`${id}-min`}
              value={minValue}
              placeholder={intl.formatMessage({
                id: 'AgeValues.minPlaceholder',
                defaultMessage: 'Min',
                description: 'Placeholder for minimum value input',
              })}
              width="50%"
              onChange={setMinValue}
            />
            <NumberInput
              name={`${id}-max`}
              value={maxValue}
              placeholder={intl.formatMessage({
                id: 'AgeValues.maxPlaceholder',
                defaultMessage: 'Max',
                description: 'Placeholder for maximum value input',
              })}
              width="50%"
              onChange={setMaxValue}
            />
          </>
        );
      }
      default:
        return (
          <NumberInput
            name={id}
            value={minValue}
            placeholder={intl.formatMessage({
              id: 'AgeValues.amountPlaceholder',
              defaultMessage: 'Amount',
              description: 'Placeholder for amount input',
            })}
            width="50%"
            onChange={setMinValue}
          />
        );
    }
  };

  return (
    <Layout spacing={2}>
      {inputs()}
      <Select
        enablePortal={true}
        isClearable={false}
        isSearchable={false}
        isMulti={false}
        name="unit"
        options={options}
        placeholder={intl.formatMessage({
          id: 'AgeValues.unitPlaceholder',
          defaultMessage: 'Unit',
          description: 'Placeholder for time unit selector',
        })}
        width="input.s.width"
        value={unitValue}
        onChange={(selectedOption) =>
          selectedOption && setUnitValue(selectedOption)
        }
      />
    </Layout>
  );
};
